import React, { memo, useState } from "react";
import { ChevronRight, Siren, User, MapPin, Calendar, DollarSign, MessageCircle, Archive, Trash2, Edit2, Package, Check, FileText, FileDown, Loader2, AlertCircle, Truck, Printer, ExternalLink } from "lucide-react";
import OrderItemRow from "./OrderItemRow";
import DaysBadge from "./DaysBadge";
import { LOGISTICS_ORDER } from "../../config/logisticsStatuses";
import { formatMoney } from "../../utils/formatters";
import { canArchiveOrder, canDeleteOrder } from "../../utils/logisticsLogic";
import { pb } from "../../services/pocketbase";
import { correiosService } from "../../services/correiosService";
import { ordersService } from "../../services/ordersService";
import { postageLabelsService } from "../../services/postageLabelsService";
import { PDFDocument } from "pdf-lib";
import { generateCertificatePDF } from "../../utils/certificateGenerator";
import { useCommunication } from "../../contexts/CommunicationContext";

// Auxiliar para carregar imagens assincronamente e converter para base64
const loadImgAsBase64 = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

// Auxiliar para recortar a etiqueta A4 original para o padrão de termica 10x15cm (utilizando as mesmas coordenadas homologadas)
const cropEtiquetaPdfBytes = async (base64) => {
  const CM_TO_PT = 28.3465;
  const pdfBytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
  const srcDoc = await PDFDocument.load(pdfBytes);
  const [srcPage] = srcDoc.getPages();
  const { width, height } = srcPage.getSize();

  const cropBox = {
    left: 10,
    bottom: height - 428,
    right: 298,
    top: height - 8
  };

  const outDoc = await PDFDocument.create();
  const embedded = await outDoc.embedPage(srcPage, cropBox);

  const larguraPt = 10 * CM_TO_PT;
  const alturaPt = 15 * CM_TO_PT;
  const page = outDoc.addPage([larguraPt, alturaPt]);

  page.drawPage(embedded, {
    x: 0,
    y: 0,
    width: larguraPt,
    height: alturaPt,
  });

  return await outDoc.save();
};

// Auxiliar para gerar o PDF da DANFE Simplificada idêntico ao leiaute e estilos do HTML original
const generateDanfeSimplificadaPDF = async (groupOrOrder) => {
  const invoice = groupOrOrder.activeInvoice || (groupOrOrder.items?.[0]?.expand?.fiscal_invoices_via_order || []).sort((a, b) => new Date(b.created) - new Date(a.created))[0];
  if (!invoice) return null;

  try {
    const cnpjEmissor = invoice.cnpj_emissor || invoice.full_data?.cnpj_emissor || "";
    const configs = await pb.collection("fiscal_configs").getFullList();
    const config = configs.find(
      (c) => c.cnpj.replace(/\D/g, "") === cnpjEmissor.replace(/\D/g, "")
    ) || configs[0] || {};
    
    const firstItem = groupOrOrder.items?.[0] || {};
    const shippingAddress = firstItem.shipping?.modified_address || firstItem.shipping?.address || {};
    const customer = firstItem.order?.customer || {};

    const formatKey = (k) => {
      return k ? k.replace(/(\d{4})/g, "$1 ").trim() : "";
    };

    const doc = new window.jspdf.jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [100, 150]
    });

    const margin = 4;
    const w = 100 - margin * 2;
    const h = 150 - margin * 2;

    // Borda externa
    doc.rect(margin, margin, w, h);

    // Título
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("DANFE SIMPLIFICADO", 50, margin + 6, { align: "center" });
    doc.line(margin, margin + 8, margin + w, margin + 8);

    // Logo / Emissor
    doc.setFontSize(8);
    doc.text("SEMPRE\nJOIAS", margin + 2, margin + 13);
    doc.line(margin + 20, margin + 8, margin + 20, margin + 25);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);
    const emitterText = [
      config.razao_social || "SEMPRE JOIAS LTDA",
      `CNPJ: ${config.cnpj || "46.223.109/0001-07"}`,
      config.endereco || "AV WALDEMAR CARLOS PEREIRA, 179 - SALA 6",
      `${config.bairro || "VILA MATILDE"} - ${config.cidade || "SAO PAULO"} - ${config.uf || "SP"} - CEP: ${config.cep || "03533-001"}`
    ];
    doc.text(emitterText, margin + 22, margin + 12);
    doc.line(margin, margin + 25, margin + w, margin + 25);

    // Código de Barras
    try {
      const barcodeUrl = `https://bwipjs-api.metafloor.com/?bcid=code128&text=${invoice.nfe_chave}&scale=3`;
      const base64Img = await loadImgAsBase64(barcodeUrl);
      if (base64Img) {
        doc.addImage(base64Img, "PNG", margin + 6, margin + 27, w - 12, 12);
      }
    } catch (e) {
      console.error("Erro ao desenhar código de barras no PDF:", e);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.text(formatKey(invoice.nfe_chave), 50, margin + 43, { align: "center" });
    doc.line(margin, margin + 45, margin + w, margin + 45);

    // Dados da NF
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    const emiDate = new Date(invoice.nfe_data_emissao || invoice.created).toLocaleDateString("pt-BR");
    doc.text(`Nº: ${invoice.nfe_numero}`, margin + 2, margin + 49);
    doc.text(`SÉRIE: ${invoice.nfe_serie}`, 50, margin + 49, { align: "center" });
    doc.text(`EMISSÃO: ${emiDate}`, margin + w - 2, margin + 49, { align: "right" });
    doc.line(margin, margin + 51, margin + w, margin + 51);

    // Título Destinatário
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, margin + 51, w, 6, "F");
    doc.rect(margin, margin + 51, w, 6, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.text("DESTINATÁRIO", margin + 2, margin + 55);

    // Nome
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    const destName = shippingAddress.recipient || customer.name || groupOrOrder.customerName;
    doc.setFont("helvetica", "bold");
    doc.text(destName, margin + 2, margin + 62);

    // Endereço
    doc.setFont("helvetica", "normal");
    const destAddress = [
      `${shippingAddress.street || ""}, ${shippingAddress.number || "SN"} ${shippingAddress.complemento ? '- ' + shippingAddress.complemento : ''}`,
      `${shippingAddress.district || ""} - ${shippingAddress.city || ""}/${(shippingAddress.statecode || shippingAddress.state || "").toUpperCase()}`,
      `CEP: ${shippingAddress.zip || ""}`
    ];
    doc.text(destAddress, margin + 2, margin + 66);

    return doc;
  } catch (err) {
    console.error("Erro ao gerar PDF da DANFE Simplificada:", err);
    return null;
  }
};

// Helper Anti-ND repetido para independência do componente
const hasValue = (val) => {
  if (!val) return false;
  const s = String(val).trim().toUpperCase();
  return s !== "" && s !== "ND" && s !== "-" && s !== "N/A";
};

// Helper interno extraído de OrdersTab
const getOrderUF = (group) => {
  if (group.customerState) return group.customerState;
  const item = group.items?.[0];
  if (!item) return "-";
  if (item.shipping?.modified_address?.statecode) return item.shipping.modified_address.statecode;
  if (item.shipping?.address?.statecode) return item.shipping.address.statecode;
  if (item.shipping?.modified_address?.state) return item.shipping.modified_address.state;
  if (item.shipping?.address?.state) return item.shipping.address.state;
  if (item.order?.shipping_address?.state) return item.order.shipping_address.state;
  if (item.order?.customer?.state) return item.order.customer.state;
  return "-";
};

const OrderGroupCard = memo(({
  user,
  group,
  isExpanded,
  isSelected, // Recebe prop booleana para preservar o React.memo()
  onToggleExpand,
  onToggleSelection, // Nova prop para Pedidos
  onEditItem,
  onMoveItem,
  onToggleTransit,
  onUpdateItemStatus,
  onUpdateLogisticsStatus,
  onSendWhatsApp,
  onArchiveOrder,
  onDeleteOrder,
  onEditOrderGroup,
  onEmitInvoice,
  loadingStep: parentLoadingStep, // Se fornecido
  onRefreshGroup,
  findCatalogItem
}) => {
  const { openQuickOrder } = useCommunication();
  const uf = getOrderUF(group);
  const hasExceptionInGroup = group.items.some((i) => hasValue(i.exception));
  const [loadingStep, setLoadingStep] = useState("");
  const [showFreeShippingModal, setShowFreeShippingModal] = useState(false);

  const firstItem = group.items?.[0] || {};
  const invoices = firstItem.expand?.fiscal_invoices_via_order || [];
  const sortedInvoices = [...invoices].sort((a, b) => new Date(b.created) - new Date(a.created));
  const isRepostagem = String(group.orderNumber || "").toLowerCase().includes("repostagem");
  const authSaidaInvoice = isRepostagem
    ? sortedInvoices.find(inv => inv.tipo_nota === "garantia_saida" && inv.nfe_status === "autorizada")
    : (sortedInvoices.find(inv => (inv.tipo_nota === "garantia_saida" || inv.tipo_nota === "venda") && inv.nfe_status === "autorizada") ||
       sortedInvoices.find(inv => inv.nfe_status === "autorizada"));
  const activeInvoice = authSaidaInvoice || (sortedInvoices.length > 0 ? sortedInvoices[0] : null);
  const nfePdf = activeInvoice ? (activeInvoice.nfe_pdf || activeInvoice.nfe_pdf_url) : "";
  const nfeXml = activeInvoice ? (activeInvoice.nfe_xml || activeInvoice.nfe_xml_url) : "";

  const handlePrintDanfeLabel = async () => {
    const firstItem = group.items?.[0] || {};
    const invoices = firstItem.expand?.fiscal_invoices_via_order || [];
    const invoice = invoices.length > 0
      ? [...invoices].sort((a, b) => new Date(b.created) - new Date(a.created))[0]
      : null;

    if (!invoice) {
      alert("Nenhuma nota fiscal encontrada.");
      return;
    }
    
    try {
      const cnpjEmissor = invoice.cnpj_emissor || invoice.full_data?.cnpj_emissor || "";
      const configs = await pb.collection("fiscal_configs").getFullList();
      const config = configs.find(
        (c) => c.cnpj.replace(/\D/g, "") === cnpjEmissor.replace(/\D/g, "")
      ) || configs[0] || {};
      
      const shippingAddress = firstItem.shipping?.modified_address || firstItem.shipping?.address || {};
      const customer = firstItem.order?.customer || {};
      
      const formatKey = (k) => {
        return k ? k.replace(/(\d{4})/g, "$1 ").trim() : "";
      };

      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
        <head>
          <title>DANFE Simplificado #${invoice.nfe_numero}</title>
          <style>
            @page {
              size: 100mm 150mm;
              margin: 0;
            }
            body {
              width: 100mm;
              height: 150mm;
              margin: 0;
              padding: 2mm;
              box-sizing: border-box;
              font-family: Arial, sans-serif;
              font-size: 10px;
              color: #000;
            }
            .container {
              border: 1px solid #000;
              height: 135mm;
              padding: 2mm;
              display: flex;
              flex-direction: column;
              box-sizing: border-box;
            }
            .header {
              display: flex;
              border-bottom: 1px solid #000;
              padding-bottom: 1.5mm;
              margin-bottom: 1.5mm;
            }
            .logo {
              width: 25%;
              font-size: 9px;
              font-weight: bold;
              text-align: center;
              border-right: 1px solid #000;
              padding-right: 1mm;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .emitter {
              width: 75%;
              padding-left: 2mm;
              font-size: 7.5px;
              line-height: 1.2;
            }
            .title-box {
              text-align: center;
              border-bottom: 1px solid #000;
              font-weight: bold;
              font-size: 11px;
              padding-bottom: 1mm;
              margin-bottom: 1.5mm;
            }
            .barcode-container {
              text-align: center;
              margin-bottom: 1.5mm;
            }
            .barcode-img {
              max-width: 95%;
              height: 12mm;
            }
            .key-text {
              font-size: 7.5px;
              text-align: center;
              font-family: monospace;
              margin-top: 1mm;
            }
            .nfe-details {
              display: flex;
              justify-content: space-between;
              border-bottom: 1px solid #000;
              border-top: 1px solid #000;
              padding: 1mm 0;
              margin-bottom: 1.5mm;
              font-weight: bold;
              font-size: 9px;
            }
            .section-title {
              font-weight: bold;
              font-size: 8.5px;
              text-transform: uppercase;
              background: #f0f0f0;
              padding: 0.5mm 1mm;
              margin-bottom: 1mm;
              border: 0.5px solid #000;
            }
            .destinatario {
              flex: 1;
              font-size: 8.5px;
              line-height: 1.3;
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="title-box">DANFE SIMPLIFICADO</div>
            
            <div class="header">
              <div class="logo">SEMPRE<br>JOIAS</div>
              <div class="emitter">
                <strong>${config.razao_social || "SEMPRE JOIAS LTDA"}</strong><br>
                CNPJ: ${config.cnpj || "46.223.109/0001-07"}<br>
                ${config.endereco || "AV WALDEMAR CARLOS PEREIRA, 179 - SALA 6"}<br>
                ${config.bairro || "VILA MATILDE"} - ${config.uf || "SP"} - CEP: ${config.cep || "03533-001"}
              </div>
            </div>

            <div class="barcode-container">
              <img class="barcode-img" src="https://bwipjs-api.metafloor.com/?bcid=code128&text=${invoice.nfe_chave}&scale=3" /><br>
              <div class="key-text">${formatKey(invoice.nfe_chave)}</div>
            </div>

            <div class="nfe-details">
              <span>Nº: ${invoice.nfe_numero}</span>
              <span>SÉRIE: ${invoice.nfe_serie}</span>
              <span>EMISSÃO: ${new Date(invoice.nfe_data_emissao || invoice.created).toLocaleDateString("pt-BR")}</span>
            </div>

            <div class="section-title">Destinatário</div>
            <div class="destinatario">
              <strong>${shippingAddress.recipient || customer.name || group.customerName}</strong><br>
              ${shippingAddress.street || ""}, ${shippingAddress.number || "SN"} ${shippingAddress.complemento ? '- ' + shippingAddress.complemento : ''}<br>
              ${shippingAddress.district || ""} - ${shippingAddress.city || ""}/${(shippingAddress.statecode || shippingAddress.state || "").toUpperCase()}<br>
              CEP: ${shippingAddress.zip || ""}
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    } catch (err) {
      alert("Erro ao gerar etiqueta de DANFE Simplificado: " + err.message);
    }
  };

  const handlePrintComboUnified = async (e) => {
    if (e) e.stopPropagation();
    const orderNum = group.orderNumber;
    const firstItem = group.items?.[0] || {};
    const invoices = firstItem.expand?.fiscal_invoices_via_order || [];
    const activeInvoice = invoices.length > 0
      ? [...invoices].sort((a, b) => new Date(b.created) - new Date(a.created))[0]
      : null;

    setLoadingStep("Combo...");
    try {
      // 1. Busca cache de postage_labels
      let shipment = await postageLabelsService.getShipment(orderNum);
      const pdfsToMerge = []; // Array de ArrayBuffers

      // --- ETAPA A: DANFE SIMPLIFICADA ---
      let danfeBlob = null;

      // Primeiro verifica se há no cache postage_labels
      if (shipment && shipment.danfe_pdf) {
        const fileUrl = pb.files.getUrl(shipment, shipment.danfe_pdf, { token: pb.authStore.token });
        const res = await fetch(fileUrl);
        if (res.ok) {
          danfeBlob = await res.blob();
        }
      }

      // Fallback: se não estiver no postage_labels, gera a DANFE Simplificada formatada em PDF usando jsPDF
      if (!danfeBlob) {
        const invoice = activeInvoice || (firstItem.expand?.fiscal_invoices_via_order || []).sort((a, b) => new Date(b.created) - new Date(a.created))[0];
        if (invoice) {
          setLoadingStep("DANFE...");
          const pdfDoc = await generateDanfeSimplificadaPDF(group);
          if (pdfDoc) {
            const arrayBuf = pdfDoc.output("arraybuffer");
            danfeBlob = new Blob([arrayBuf], { type: "application/pdf" });
            try {
              await postageLabelsService.uploadPdf(orderNum, "danfe_pdf", danfeBlob, `danfe_${orderNum}.pdf`);
            } catch (err) {
              console.warn("Falha ao salvar DANFE no cache:", err);
            }
          }
        }
      }

      // Se achou, adiciona aos PDFs a serem mesclados
      if (danfeBlob) {
        const arrayBuf = await danfeBlob.arrayBuffer();
        pdfsToMerge.push(arrayBuf);
      } else {
        // Se a nota não foi emitida, avisa o usuário
        if (!activeInvoice) {
          const emit = window.confirm(`A Nota Fiscal do pedido #${orderNum} ainda não foi emitida. Deseja emitir agora ou continuar sem a DANFE?`);
          if (emit) {
            if (onEmitInvoice) onEmitInvoice(group);
            return;
          }
        }
      }

      // --- ETAPA B: ETIQUETA CORREIOS ---
      let labelBlob = null;

      // Primeiro verifica se há no cache postage_labels
      if (shipment && shipment.etiqueta_pdf) {
        const fileUrl = pb.files.getUrl(shipment, shipment.etiqueta_pdf, { token: pb.authStore.token });
        const res = await fetch(fileUrl);
        if (res.ok) {
          labelBlob = await res.blob();
        }
      }

      // Fallback: se não estiver cacheada, mas temos o ID de pré-postagem, tentamos baixar da API
      const prepostagemId = firstItem.full_data?.correios_prepostagem_id;
      
      if (!labelBlob && prepostagemId) {
        setLoadingStep("Etiqueta...");
        const idRecibo = await correiosService.gerarRotulo(prepostagemId);
        const base64 = await correiosService.baixarRotulo(idRecibo);
        
        // Recorta a etiqueta para 10x15cm
        const croppedBytes = await cropEtiquetaPdfBytes(base64);
        labelBlob = new Blob([croppedBytes], { type: "application/pdf" });

        // Salva no banco de dados para evitar re-geração
        try {
          await postageLabelsService.saveShipment(orderNum, firstItem.full_data?.correios_codigo_objeto || "", prepostagemId);
          await postageLabelsService.uploadPdf(orderNum, "etiqueta_pdf", labelBlob, `etiqueta_${orderNum}.pdf`);
        } catch (err) {
          console.warn("Falha ao salvar etiqueta no cache:", err);
        }
      }

      if (labelBlob) {
        const arrayBuf = await labelBlob.arrayBuffer();
        pdfsToMerge.push(arrayBuf);
      } else {
        const genLabel = window.confirm(`A etiqueta de envio dos Correios para o pedido #${orderNum} ainda não foi gerada. Deseja gerar agora?`);
        if (genLabel) {
          handleCorreiosLabel(e);
          return;
        }
      }

      // --- ETAPA C: CERTIFICADOS ---
      let certBlob = null;

      if (shipment && shipment.certificados_pdf) {
        const fileUrl = pb.files.getUrl(shipment, shipment.certificados_pdf, { token: pb.authStore.token });
        const res = await fetch(fileUrl);
        if (res.ok) {
          certBlob = await res.blob();
        }
      }

      // Fallback: se não estiver no cache, gera dinamicamente no frontend
      if (!certBlob) {
        setLoadingStep("Certificados...");
        // passamos returnDoc = true para obter o doc do jsPDF
        const pdfDoc = generateCertificatePDF(group.items, findCatalogItem, true);
        if (pdfDoc) {
          // Obtém o ArrayBuffer direto do jsPDF
          const arrayBuf = pdfDoc.output("arraybuffer");
          certBlob = new Blob([arrayBuf], { type: "application/pdf" });
          
          // Marca no banco de dados como impresso
          await ordersService.markCertificatesPrinted(group.items);

          // Salva no cache do postage_labels
          try {
            await postageLabelsService.uploadPdf(orderNum, "certificados_pdf", certBlob, `certificados_${orderNum}.pdf`);
          } catch (err) {
            console.warn("Falha ao salvar certificados no cache:", err);
          }
        }
      }

      if (certBlob) {
        const arrayBuf = await certBlob.arrayBuffer();
        pdfsToMerge.push(arrayBuf);
      }

      // --- ETAPA D: MESCLAR E EXIBIR ---
      if (pdfsToMerge.length === 0) {
        throw new Error("Nenhum PDF disponível para impressão.");
      }

      setLoadingStep("Mesclando...");
      const mergedPdfDoc = await PDFDocument.create();
      for (const pdfBytes of pdfsToMerge) {
        try {
          const doc = await PDFDocument.load(pdfBytes);
          const copiedPages = await mergedPdfDoc.copyPages(doc, doc.getPageIndices());
          copiedPages.forEach((page) => mergedPdfDoc.addPage(page));
        } catch (loadErr) {
          console.error("Erro ao carregar PDF individual para mesclagem:", loadErr);
        }
      }

      const mergedPdfBytes = await mergedPdfDoc.save();
      const mergedBlob = new Blob([mergedPdfBytes], { type: "application/pdf" });
      const mergedUrl = URL.createObjectURL(mergedBlob);

      setLoadingStep("Imprimindo...");
      window.open(mergedUrl, "_blank");
    } catch (err) {
      console.error(err);
      alert(`Falha ao imprimir combo do pedido #${orderNum}: ${err.message}`);
    } finally {
      setLoadingStep("");
    }
  };

  const handleSelectMotoboy = async () => {
    try {
      setLoadingStep("Movendo para MOTOBOY...");
      await onUpdateLogisticsStatus(group, "MOTOBOY");
      alert("Pedido direcionado para o status MOTOBOY!");
    } catch (err) {
      alert("Erro ao mover para MOTOBOY: " + err.message);
    } finally {
      setLoadingStep("");
    }
  };



  const handleCorreiosLabel = (e) => {
    if (e) e.stopPropagation();
    const firstItem = group.items?.[0] || {};
    const hasExistingLabel = !!firstItem.full_data?.correios_prepostagem_id;

    const shippingMethodUpper = String(group.shippingMethod || "").trim().toUpperCase();
    const isDirectSedex = shippingMethodUpper.includes("SEDEX") || shippingMethodUpper.includes("03220");
    const isDirectPac = shippingMethodUpper.includes("PAC") || shippingMethodUpper.includes("03298");
    const shouldShowModal = !isDirectSedex && !isDirectPac;

    if (shouldShowModal && !hasExistingLabel) {
      setShowFreeShippingModal(true);
    } else {
      generateLabelWithService(null);
    }
  };

  const generateLabelWithService = async (chosenServiceCode) => {
    const firstItem = group.items?.[0] || {};
    const hasExistingLabel = !!firstItem.full_data?.correios_prepostagem_id;
    const prepostId = firstItem.full_data?.correios_prepostagem_id;

    setLoadingStep(hasExistingLabel ? "Reimprimindo..." : "Autenticando...");
    try {
      let activePrepostId = prepostId;
      let codigoObjeto = firstItem.full_data?.correios_codigo_objeto || "";

      if (!hasExistingLabel) {
        const shippingAddress = firstItem.shipping?.modified_address || firstItem.shipping?.address || {};
        const customer = firstItem.order?.customer || {};

        const cleanCep = (shippingAddress.zip || "").replace(/\D/g, "");
        if (cleanCep.length !== 8) {
          throw new Error("CEP do destinatário inválido ou incompleto.");
        }

        const rawPhone = (customer.phone || "").replace(/\D/g, "");
        let ddd = "11";
        let phoneNum = "";
        if (rawPhone.length >= 10) {
          ddd = rawPhone.substring(0, 2);
          phoneNum = rawPhone.substring(2);
        } else {
          phoneNum = rawPhone || "999999999";
        }

        const cleanCpf = (customer.cpf || "").replace(/\D/g, "");

        setLoadingStep("Criando pré-postagem...");

        let codigoServico = chosenServiceCode;
        if (!codigoServico) {
          codigoServico = "03298"; // PAC com contrato padrão
          const shippingMethodUpper = String(group.shippingMethod || "").toUpperCase();
          if (shippingMethodUpper.includes("SEDEX 10") || shippingMethodUpper.includes("SEDEX10") || shippingMethodUpper.includes("SEDEX-10") || shippingMethodUpper.includes("03158")) {
            codigoServico = "03158"; // SEDEX 10
          } else if (shippingMethodUpper.includes("SEDEX 12") || shippingMethodUpper.includes("SEDEX12") || shippingMethodUpper.includes("SEDEX-12") || shippingMethodUpper.includes("03140")) {
            codigoServico = "03140"; // SEDEX 12
          } else if (shippingMethodUpper.includes("SEDEX") || shippingMethodUpper.includes("03220")) {
            codigoServico = "03220"; // SEDEX com contrato
          } else if (shippingMethodUpper.includes("PAC") || shippingMethodUpper.includes("03298")) {
            codigoServico = "03298"; // PAC com contrato
          }
        }

        const dadosPedido = {
          codigoServico,
          chaveNFe: activeInvoice?.nfe_chave || "",
          numeroNotaFiscal: activeInvoice?.nfe_numero || "",
          destinatario: {
            nome: shippingAddress.recipient || customer.name || group.customerName,
            cpfCnpj: cleanCpf,
            dddCelular: ddd,
            celular: phoneNum,
            email: customer.email || "cliente@email.com",
            endereco: {
              cep: cleanCep,
              logradouro: shippingAddress.street || "",
              numero: shippingAddress.number || "SN",
              complemento: shippingAddress.complemento || "",
              bairro: shippingAddress.district || "",
              cidade: shippingAddress.city || "",
              uf: (shippingAddress.statecode || shippingAddress.state || "").toUpperCase()
            }
          },
          valorDeclaracao: group.totalValue.toFixed(2),
          conteudoDeclaracao: group.items.map(i => i.sku).join(", ").substring(0, 40) || "Joia em prata 925"
        };

        const result = await correiosService.criarPrePostagem(dadosPedido);
        activePrepostId = result.id;
        codigoObjeto = result.codigoObjeto;
      }

      setLoadingStep("Gerando rótulo...");
      const idRecibo = await correiosService.gerarRotulo(activePrepostId);

      setLoadingStep("Processando rótulo...");
      const base64Pdf = await correiosService.baixarRotulo(idRecibo);

      // Abre o PDF apenas se for reimpressão (já existia etiqueta)
      if (hasExistingLabel) {
        setLoadingStep("Abrindo etiqueta...");
        await correiosService.abrirEtiquetaPDF(base64Pdf);
      }

      if (!hasExistingLabel) {
        await ordersService.saveCorreiosDetails(group.items, activePrepostId, codigoObjeto);
      }
      alert(hasExistingLabel ? "Etiqueta reaberta com sucesso!" : "Etiqueta gerada e vinculada com sucesso!");
    } catch (err) {
      console.error(err);
      alert(`Falha no processo dos Correios: ${err.message}`);
    } finally {
      setLoadingStep("");
    }
  };

  console.log('3. FILHO RENDERIZOU, isSelected:', isSelected);

  return (
    <div
      className={`bg-white rounded-xl border ${
        hasExceptionInGroup
          ? "border-red-200 ring-1 ring-red-100"
          : "border-slate-200"
      } shadow-sm overflow-hidden hover:shadow-md transition-all`}
    >
      {/* HEADER DO PEDIDO */}
      <div
        className="flex flex-col xl:flex-row xl:flex-wrap xl:items-center justify-between p-4 cursor-pointer relative gap-y-4 xl:gap-y-0"
        onClick={() => onToggleExpand(String(group.orderNumber))}
      >
        {/* 1. Checkbox, Expand, Pedido e Cliente */}
        <div className="flex items-center gap-3 w-full xl:w-auto overflow-hidden">
          <div 
            className="flex items-center gap-2 p-1 cursor-pointer"
            onClick={(e) => {
              console.log('1. CLIQUE DISPARADO', group.orderNumber);
              e.stopPropagation();
              onToggleSelection(String(group.orderNumber));
            }}
          >
            <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
              isSelected ? "bg-blue-500 border-blue-500" : "bg-white border-slate-300"
            }`}>
              {isSelected && <Check size={12} strokeWidth={4} className="text-white" />}
            </div>
          </div>
          <div
            className={`p-1 rounded transition-transform ${
              isExpanded ? "rotate-90" : ""
            }`}
          >
            <ChevronRight size={18} className="text-slate-400" />
          </div>
          <div className="w-px h-8 bg-slate-200 mx-2 hidden xl:block"></div>
          <div className="flex flex-col overflow-hidden w-28 pr-2">
            <span className="text-[10px] font-bold text-slate-400 uppercase">
              Pedido
            </span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openQuickOrder(group.orderNumber);
              }}
              className="text-blue-700 hover:text-blue-900 hover:underline font-extrabold text-lg tracking-tight flex items-center gap-1 text-left cursor-pointer transition group/order"
              title="Abrir Ficha Rápida do Pedido"
            >
              <span>#{group.orderNumber}</span>
              <ExternalLink size={12} className="opacity-0 group-hover/order:opacity-100 transition text-blue-500 shrink-0" />
              {hasExceptionInGroup && (
                <Siren size={16} className="text-red-600 animate-pulse ml-0.5" />
              )}
            </button>
          </div>
          <div className="w-px h-8 bg-slate-200 mx-2 hidden xl:block"></div>
          <div className="flex flex-col overflow-hidden min-w-[120px]">
            <span className="text-[10px] font-bold text-slate-400 uppercase">
              Cliente
            </span>
            <div className="flex items-center gap-1 truncate">
              <User size={12} className="text-slate-400" />
              <span className="font-bold text-slate-700 truncate text-sm">
                {typeof group.customerName === "object"
                  ? group.customerName?.name || "Cliente sem nome"
                  : group.customerName || "Cliente sem nome"}
              </span>
            </div>
          </div>
        </div>

        {/* 2. UF, Frete e Status */}
        <div className="flex items-center gap-2 w-full xl:w-auto pl-8 xl:pl-0">
          <div className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-bold flex items-center gap-1 border border-slate-200 uppercase">
            <MapPin size={10} /> {uf}
          </div>
          <div className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs font-bold border border-slate-200 truncate max-w-[100px]">
            {group.shippingMethod || "Retirada"}
          </div>
        </div>

        <div
          className="w-full xl:w-32 pl-8 xl:pl-0"
          onClick={(e) => e.stopPropagation()}
        >
          <select
            className="w-full text-xs font-bold px-2 py-1.5 rounded border border-slate-300 bg-white outline-none cursor-pointer hover:border-blue-400 focus:ring-2 focus:ring-blue-100"
            value={group.logisticsStatus}
            onChange={(e) => onUpdateLogisticsStatus(group, e.target.value)}
          >
            {LOGISTICS_ORDER.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        {/* 3. Meta Info */}
        <div className="flex items-center gap-3 justify-between xl:justify-end text-sm xl:w-auto w-full border-t xl:border-t-0 pt-2 xl:pt-0 mt-2 xl:mt-0 pl-8 xl:pl-0">
          {/* BADGE DE DIAS / ANÁLISE TEMPORAL DA OFICINA */}
          <div className="scale-90 origin-center shrink-0" onClick={(e) => e.stopPropagation()}>
            <DaysBadge
              date={group.items?.[0]?.customCreatedAt || group.items?.[0]?.createdAt || group.date}
              order={group}
            />
          </div>
          <div className="flex flex-col items-end">
            <span className="text-[10px] font-bold text-slate-400 uppercase mb-0.5">
              Data
            </span>
            <span className="font-medium text-slate-600 flex items-center gap-1">
              <Calendar size={12} /> {group.date.toLocaleDateString("pt-BR")}
            </span>
          </div>
          <div className="flex flex-col items-end w-20">
            <span className="text-[10px] font-bold text-slate-400 uppercase">
              Total
            </span>
            <span className="font-bold text-emerald-600 flex items-center gap-1">
              <DollarSign size={12} /> {formatMoney(group.totalValue)}
            </span>
          </div>
          <div
            className="flex items-center gap-1 pl-2 border-l border-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            {/* --- WHATSAPP --- */}
            <button
              onClick={() => onSendWhatsApp(group)}
              className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
              title="Enviar Status do Pedido via WhatsApp"
            >
              <MessageCircle size={16} />
            </button>
            {canArchiveOrder(group) && (
              <button
                onClick={() => onArchiveOrder(group)}
                className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                title="Arquivar"
              >
                <Archive size={16} />
              </button>
            )}
            {canDeleteOrder(group) && (
              <button
                onClick={() => onDeleteOrder(group)}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                title="Excluir"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              onClick={() => onEditOrderGroup(group)}
              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
              title="Editar Dados"
            >
              <Edit2 size={16} />
            </button>

            {/* --- NOTA FISCAL (NF-e) --- */}
            {(() => {
              const firstItem = group.items?.[0] || {};
              const invoices = firstItem.expand?.fiscal_invoices_via_order || [];
              const activeInvoice = invoices.length > 0
                ? [...invoices].sort((a, b) => new Date(b.created) - new Date(a.created))[0]
                : null;

              const nfeStatus = activeInvoice ? activeInvoice.nfe_status : "pendente";
              const nfeError = activeInvoice ? activeInvoice.nfe_error_message : "";
              const nfeNum = activeInvoice ? activeInvoice.nfe_numero : "";
              const nfeSerie = activeInvoice ? activeInvoice.nfe_serie : "";
              const nfeAmbiente = activeInvoice ? activeInvoice.ambiente : "homologacao";
              const nfePdf = activeInvoice ? (activeInvoice.nfe_pdf || activeInvoice.nfe_pdf_url) : "";
              const nfeXml = activeInvoice ? (activeInvoice.nfe_xml || activeInvoice.nfe_xml_url) : "";

              const getFullUrl = (path) => {
                if (!path) return "";
                let cleanPath = path;
                if (cleanPath.startsWith("http://arquivos_development") || cleanPath.startsWith("https://arquivos_development")) {
                  return cleanPath.replace(/^https?:\/\/arquivos_development/, "https://homologacao.focusnfe.com.br/arquivos_development");
                }
                if (cleanPath.startsWith("http://arquivos") || cleanPath.startsWith("https://arquivos")) {
                  return cleanPath.replace(/^https?:\/\/arquivos/, "https://api.focusnfe.com.br/arquivos");
                }
                if (cleanPath.startsWith("http")) return cleanPath;
                const base = nfeAmbiente === "producao" ? "https://api.focusnfe.com.br" : "https://homologacao.focusnfe.com.br";
                return base + cleanPath;
              };

              const pdfUrl = activeInvoice?.nfe_pdf ? pb.files.getUrl(activeInvoice, activeInvoice.nfe_pdf, { token: pb.authStore.token }) : (activeInvoice?.nfe_pdf_url ? getFullUrl(activeInvoice.nfe_pdf_url) : "");
              const xmlUrl = activeInvoice?.nfe_xml ? pb.files.getUrl(activeInvoice, activeInvoice.nfe_xml, { token: pb.authStore.token }) : (activeInvoice?.nfe_xml_url ? getFullUrl(activeInvoice.nfe_xml_url) : "");

              if (nfeStatus === "processando") {
                return (
                  <div 
                    className="flex items-center gap-1 px-1.5 py-1 text-[10px] text-blue-600 font-bold bg-blue-50 rounded animate-pulse" 
                    title="Focus NFe processando nota fiscal na SEFAZ..."
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Loader2 size={12} className="animate-spin" />
                    <span>NF-e...</span>
                  </div>
                );
              }

              if (nfeStatus === "autorizada") {
                return (
                  <div className="flex items-center gap-1 border-l pl-2 border-slate-100" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[9px] font-black text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded mr-1">
                      NF #{nfeNum}/{nfeSerie}
                    </span>
                    {nfePdf && (
                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded"
                        title="Visualizar DANFE (PDF)"
                      >
                        <FileText size={16} />
                      </a>
                    )}
                    <button
                      onClick={handlePrintDanfeLabel}
                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      title="Imprimir DANFE Simplificado (Etiqueta 10x15)"
                    >
                      <Printer size={16} />
                    </button>
                    {nfeXml && (
                      <a
                        href={xmlUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Download XML"
                        download={`NFe_${nfeNum}.xml`}
                      >
                        <FileDown size={16} />
                      </a>
                    )}
                  </div>
                );
              }

              return (
                <div className="flex items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                  {nfeStatus === "erro" && (
                    <div 
                      className="p-1 text-red-500 hover:bg-red-55 rounded cursor-help"
                      title={`Rejeição SEFAZ: ${nfeError || "Erro na emissão"}`}
                    >
                      <AlertCircle size={14} className="animate-bounce" />
                    </div>
                  )}
                  <button
                    onClick={() => onEmitInvoice(group)}
                    className={`p-1.5 rounded transition-colors ${
                      nfeStatus === "erro" 
                        ? "text-red-650 hover:bg-red-50 hover:text-red-800 text-red-650 bg-red-50/50" 
                        : "text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                    }`}
                    title={nfeStatus === "erro" ? "Corrigir e Emitir Nota Fiscal" : "Emitir Nota Fiscal (NF-e)"}
                  >
                    <FileText size={16} />
                  </button>
                </div>
              );
            })()}

            {/* --- ETIQUETA DOS CORREIOS --- */}
            {(() => {
              const firstItem = group.items?.[0] || {};
              const hasLabel = !!firstItem.full_data?.correios_prepostagem_id;
              const isAguardandoPostagem = group.logisticsStatus === "AGUARDANDO POSTAGEM";
              const isRetiradaOuMotoboy = /retirada|motoboy/i.test(group.shippingMethod || "");
              const showCorreiosButton = (isAguardandoPostagem && !isRetiradaOuMotoboy) || hasLabel;

              if (!showCorreiosButton) return null;

              return (
                <div className="flex items-center pl-2 border-l border-slate-100" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={handleCorreiosLabel}
                    disabled={!!loadingStep}
                    className={`p-1.5 rounded transition-colors ${
                      loadingStep 
                        ? "text-blue-655 text-blue-600 bg-blue-50 animate-pulse" 
                        : hasLabel 
                          ? "text-emerald-600 hover:bg-emerald-50" 
                          : "text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                    }`}
                    title={loadingStep ? loadingStep : hasLabel ? "Reimprimir Etiqueta dos Correios" : "Gerar Etiqueta dos Correios"}
                  >
                    {loadingStep ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Truck size={16} />
                    )}
                  </button>
                </div>
              );
            })()}

            {/* --- COMBO DE IMPRESSÃO --- */}
            {(() => {
              const isAguardandoPostagem = group.logisticsStatus === "AGUARDANDO POSTAGEM";
              if (!isAguardandoPostagem) return null;

              return (
                <div className="flex items-center pl-2 border-l border-slate-100" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={handlePrintComboUnified}
                    disabled={!!loadingStep}
                    className={`p-1.5 rounded transition-colors ${
                      loadingStep === "Combo..." 
                        ? "text-indigo-600 bg-indigo-50 animate-pulse" 
                        : "text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                    }`}
                    title="Imprimir Combo Completo (DANFE + Etiqueta Correios + Certificados)"
                  >
                    <Printer size={16} />
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* LISTA DE ITENS EXPANDIDA */}
      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50/50 p-4 animate-slide-in">
          <h4 className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-2 mb-2">
            <Package size={12} /> Itens do Pedido ({group.items.length})
          </h4>
          <div className="space-y-2">
            {group.items.map((subItem) => (
              <OrderItemRow
                user={user}
                key={subItem.id}
                subItem={subItem}
                onEditItem={onEditItem}
                onMoveItem={onMoveItem}
                onToggleTransit={onToggleTransit}
                onUpdateStatus={onUpdateItemStatus}
              />
            ))}
          </div>
        </div>
      )}
      {/* Seletor de serviço para frete grátis/customizado */}
      {showFreeShippingModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in" onClick={(e) => e.stopPropagation()}>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl p-6 max-w-sm w-full mx-4 animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-extrabold text-slate-800 text-lg mb-2">Serviço de Envio</h3>
            <p className="text-slate-500 text-xs mb-4">
              O pedido <strong>#{group.orderNumber}</strong> possui o frete <strong>{group.shippingMethod || "FREE"}</strong>. Selecione qual serviço de postagem deseja utilizar para este pedido:
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => {
                  setShowFreeShippingModal(false);
                  generateLabelWithService("03298"); // PAC
                }}
                className="flex items-center justify-between p-3.5 border border-blue-200 bg-blue-50/50 hover:bg-blue-50 text-blue-700 font-extrabold rounded-xl transition-all cursor-pointer hover:scale-[1.02]"
              >
                <div className="flex items-center gap-2">
                  <span className="bg-blue-600 text-white text-[10px] font-black px-2 py-0.5 rounded">PAC</span>
                  <span className="text-xs">Postagem Econômica</span>
                </div>
                <ChevronRight size={14} />
              </button>

              <button
                onClick={() => {
                  setShowFreeShippingModal(false);
                  generateLabelWithService("03220"); // SEDEX
                }}
                className="flex items-center justify-between p-3.5 border border-red-200 bg-red-50/50 hover:bg-red-50 text-red-700 font-extrabold rounded-xl transition-all cursor-pointer hover:scale-[1.02]"
              >
                <div className="flex items-center gap-2">
                  <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded">SEDEX</span>
                  <span className="text-xs">Postagem Expressa</span>
                </div>
                <ChevronRight size={14} />
              </button>

              <button
                onClick={async () => {
                  setShowFreeShippingModal(false);
                  await handleSelectMotoboy();
                }}
                className="flex items-center justify-between p-3.5 border border-amber-200 bg-amber-50/50 hover:bg-amber-50 text-amber-700 font-extrabold rounded-xl transition-all cursor-pointer hover:scale-[1.02]"
              >
                <div className="flex items-center gap-2">
                  <span className="bg-amber-600 text-white text-[10px] font-black px-2 py-0.5 rounded">MOTOBOY</span>
                  <span className="text-xs">Entrega Local</span>
                </div>
                <ChevronRight size={14} />
              </button>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex justify-end">
              <button
                onClick={() => setShowFreeShippingModal(false)}
                className="text-xs text-slate-500 hover:text-slate-700 font-semibold px-3 py-1.5 rounded-lg"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  // Otimização de memoização profunda para evitar a quebra via object references do `useOrderProcessing`
  
  // 1. Checagem rápida de props primitivas/básicas do Card
  if (prevProps.isExpanded !== nextProps.isExpanded) return false;
  if (prevProps.isSelected !== nextProps.isSelected) return false;
  
  const pGroup = prevProps.group;
  const nGroup = nextProps.group;
  
  if (pGroup.orderNumber !== nGroup.orderNumber) return false;
  if (pGroup.logisticsStatus !== nGroup.logisticsStatus) return false;
  if (pGroup.items.length !== nGroup.items.length) return false;

  // 2. Comparação profunda dos Itens do Grupo
  for (let i = 0; i < pGroup.items.length; i++) {
    const pItem = pGroup.items[i];
    const nItem = nGroup.items[i];
    
    if (pItem.id !== nItem.id) return false;
    if (pItem.status !== nItem.status) return false;
    if (pItem.updated !== nItem.updated) return false; 
    
    if (pItem.exception !== nItem.exception) return false;
    if (pItem.transit_status !== nItem.transit_status) return false;
    if (pItem.isInterceptedPE !== nItem.isInterceptedPE) return false;
    if (pItem.printed !== nItem.printed) return false;
    if (pItem.certificatePrinted !== nItem.certificatePrinted) return false;

    // Compare full_data JSON string to capture any changes inside metadata (like nfe_status)
    if (JSON.stringify(pItem.full_data || {}) !== JSON.stringify(nItem.full_data || {})) return false;

    // Compare messages array length to trigger re-render on changes
    const pMsgsLength = Array.isArray(pItem.full_data?.messages) ? pItem.full_data.messages.length : 0;
    const nMsgsLength = Array.isArray(nItem.full_data?.messages) ? nItem.full_data.messages.length : 0;
    if (pMsgsLength !== nMsgsLength) return false;
    
    if (JSON.stringify(pItem.specs) !== JSON.stringify(nItem.specs)) return false;
  }

  // Se chegou até aqui, as referências de memória mudaram, mas OS DADOS REAIS SÃO IDÊNTICOS!
  return true;
});

export default OrderGroupCard;
